import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowRight, CheckCircle2, Sparkles, Timer, FileText, Compass, ShieldCheck, Zap } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/5922207/pexels-photo-5922207.jpeg";
const TESTIMONIAL_IMG = "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg";
const ACADEMY_IMG = "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2";
const GOV_IMG = "https://images.unsplash.com/photo-1523292562811-8fa7962a78c8";

const stats = [
  { k: "$2.4B", v: "Federal grants awarded to small business each year" },
  { k: "40+", v: "Curated grants across every US state" },
  { k: "8", v: "AI-drafted documents per application" },
  { k: "$100", v: "Flat monthly price. Cancel anytime." },
];

const features = [
  { icon: Compass, title: "Grant discovery across every US state", body: "Federal, state, and foundation grants filtered by eligibility, industry, deadline and amount. See only the ones you can actually win." },
  { icon: Sparkles, title: "AI-drafted, funder-ready documents", body: "Business plans, cover letters, project narratives, budget justifications — generated in your voice, grounded in your org profile." },
  { icon: Timer, title: "Deadline autopilot — never miss again", body: "One-click applications, deadline countdowns, and reminders. Auto-submit toggles so nothing slips through the cracks." },
  { icon: FileText, title: "Application command center", body: "Track drafts, submissions, awards. See potential funding, funding won, and next action on every grant in one dashboard." },
  { icon: ShieldCheck, title: "Bank-grade security", body: "Encrypted at rest and in transit. Rate-limited auth. Bcrypt password hashing. JWT sessions. Zero shortcuts." },
  { icon: Zap, title: "Academy — learn as you apply", body: "Bite-size lessons on EINs, 501(c)(3) filings, budget math, and the exact documents funders want. Templates included." },
];

const testimonials = [
  { name: "Aisha Bennett", role: "ED, Nonprofit — Detroit", text: "GrantFlow pulled together our RWJF submission in under 3 hours. We used to spend three weeks." },
  { name: "Marcus Ortega", role: "LLC — San Antonio", text: "The auto-apply reminder alone saved a $75K SBA deadline. Membership pays for itself in the first month." },
  { name: "Priya Rao", role: "Founder — Boston bio startup", text: "Every NIH SBIR document was funder-ready. Reviewers actually complimented the narrative structure." },
  { name: "Jordan Miles", role: "Rural farm co-op — Iowa", text: "USDA Value-Added is a nightmare of paperwork. GrantFlow made it feel like filling out a driver's license form." },
  { name: "Sofia Kim", role: "501(c)(3) — Portland", text: "We went from applying to 2 grants a year to 14. Same team, same hours." },
];

export default function Landing() {
  return (
    <div className="App">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-5" data-testid="hero-overline">FOR US NONPROFITS &amp; LLCS · EST. 2026</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-neutral-900">
              Win business grants
              <br />
              <span className="inline-block relative">
                without the paperwork.
                <span className="absolute -bottom-2 left-0 h-1.5 w-full bg-[color:var(--accent)] rounded-full" />
              </span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-neutral-600 max-w-xl leading-relaxed">
              GrantFlow finds the grants you qualify for across all 50 states, drafts every required document with AI, and
              keeps you ahead of every deadline. Built for founders and executive directors chasing real capital.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 items-center">
              <Link to="/signup" className="btn-primary" data-testid="hero-cta-primary">
                Start 14-day free trial <ArrowRight size={18} />
              </Link>
              <Link to="/grants" className="btn-ghost" data-testid="hero-cta-secondary">Browse 40+ live grants</Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-neutral-500">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-600" /> Federal · State · Foundation</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-600" /> AI docs powered by Claude Sonnet 5</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-neutral-200 aspect-[4/5] bg-neutral-100 grain">
              <img src={HERO_IMG} alt="Team collaborating on grant application" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-8 -left-6 md:-left-10 bg-white border border-neutral-200 rounded-xl shadow-lg p-4 w-64" data-testid="hero-award-card">
              <div className="overline text-neutral-500">Awarded this month</div>
              <div className="mt-1 font-display text-2xl font-bold text-neutral-900">$186,400</div>
              <div className="text-xs text-neutral-500">Aggregate to GrantFlow members</div>
            </div>
            <div className="hidden md:block absolute -top-4 -right-4 bg-[color:var(--accent)] text-white rounded-xl px-4 py-3 shadow-lg">
              <div className="text-xs uppercase tracking-widest opacity-80">Next deadline</div>
              <div className="font-display text-lg font-semibold">15 days</div>
            </div>
          </div>
        </div>

        {/* stats strip */}
        <div className="border-y border-neutral-200 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-neutral-200">
            {stats.map((s, i) => (
              <div key={i} className={`px-4 ${i === 0 ? "pl-0" : ""}`} data-testid={`stat-${i}`}>
                <div className="font-display text-3xl font-bold text-neutral-900">{s.k}</div>
                <div className="text-xs text-neutral-600 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-24" data-testid="features-section">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <div className="overline mb-3">WHAT'S INSIDE</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-neutral-900">
              Everything you need to turn grants into money in the bank.
            </h2>
          </div>
          <p className="md:col-span-5 text-neutral-600 leading-relaxed">
            GrantFlow replaces the folder of Word docs, sticky-note deadlines, and half-finished cover letters with a
            single command center that does the heavy work for you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="gf-card p-7" data-testid={`feature-card-${i}`}>
                <div className="h-11 w-11 rounded-lg bg-neutral-900 text-white flex items-center justify-center mb-4">
                  <Icon size={20} />
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Split section: government + academy */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-24 grid lg:grid-cols-2 gap-6" data-testid="split-section">
        <div className="relative rounded-2xl overflow-hidden border border-neutral-200 bg-neutral-100 min-h-[380px]">
          <img src={GOV_IMG} alt="Government grant" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
          <div className="absolute inset-0 p-8 lg:p-10 flex flex-col justify-end text-white">
            <div className="overline text-white/80">FEDERAL · STATE · FOUNDATION</div>
            <h3 className="font-display text-3xl font-bold mt-2">Real grants. Real money. Real deadlines.</h3>
            <p className="text-white/85 mt-3 max-w-md">From SBA SBIR to state-level rural innovation funds and RWJF, our database ships with 40+ live opportunities on day one.</p>
            <Link to="/grants" className="mt-6 inline-flex items-center gap-2 bg-white text-neutral-900 rounded-full px-5 py-2.5 font-medium w-fit hover:bg-neutral-100 transition-colors" data-testid="split-grants-cta">
              Browse grants <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="relative rounded-2xl overflow-hidden bg-[color:var(--accent)] text-white p-8 lg:p-10 flex flex-col justify-between min-h-[380px]">
          <div>
            <div className="overline text-white/80">ACADEMY</div>
            <h3 className="font-display text-3xl font-bold mt-2">We teach you to get your docs — not just give them to you.</h3>
            <p className="mt-3 text-white/90 max-w-md">EIN, DUNS/SAM.gov registration, 501(c)(3) status, budget spreadsheets — the exact stack of paperwork funders check first.</p>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm"><div className="text-xs uppercase opacity-80">Lesson</div><div className="font-semibold">Register on SAM.gov</div></div>
            <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm"><div className="text-xs uppercase opacity-80">Template</div><div className="font-semibold">3-year budget model</div></div>
          </div>
          <img src={ACADEMY_IMG} alt="Academy" className="hidden md:block absolute -bottom-14 -right-14 h-56 w-56 rounded-full object-cover border-4 border-white shadow-2xl" />
        </div>
      </section>

      {/* Testimonials rail */}
      <section className="mt-24 py-16 border-y border-neutral-200 bg-neutral-50 overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="overline mb-3">TESTIMONIALS</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Members are winning. Fast.</h2>
          </div>
          <div className="flex items-center gap-4">
            <img src={TESTIMONIAL_IMG} alt="testimonial" className="w-14 h-14 rounded-full object-cover border-2 border-white shadow" />
            <div>
              <div className="font-medium">"Best $100 I spend every month."</div>
              <div className="text-xs text-neutral-500">— Marcus O., LLC founder</div>
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="flex gap-5 marquee-track w-max">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div key={i} className="gf-card w-[340px] p-6 shrink-0" data-testid={`t-card-${i}`}>
                <p className="text-neutral-800 leading-relaxed">"{t.text}"</p>
                <div className="mt-4 text-sm">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-neutral-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 pt-24 text-center" data-testid="pricing-preview">
        <div className="overline mb-3">FLAT PRICING</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">$100/month. Everything included.</h2>
        <p className="mt-4 text-neutral-600 max-w-2xl mx-auto">One grant awarded pays for a decade of membership. Save 20% with the annual plan.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/pricing" className="btn-primary" data-testid="pricing-cta">See plans <ArrowRight size={18} /></Link>
          <Link to="/signup" className="btn-ghost" data-testid="signup-cta">Create free account</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
